import { analyticsEventSchema, contentFeedSchema, installSessionSchema, ocrResponseSchema, shelterBlockListSchema, shelterFeedSchema, type AnalyticsEvent, type ContentCard, type OcrResponse, type ReminderPreference, type ShelterBlock, type ShelterFeedItem } from '@wolhasudang/shared';
import { z } from 'zod';
import { db } from './db.js';
import { platform } from './platform.js';

interface StoredInstallSession {
  installId: string;
  installToken: string;
  expiresAt: number;
}

const sessionResponseSchema = installSessionSchema.extend({ expiresAt: z.number().int().positive() });

export class ApiFailure extends Error {
  constructor(readonly code: string, message: string, readonly status: number) { super(message); }
}

export async function getShelterFeed(): Promise<{ items: ShelterFeedItem[]; nextCursor: string | null; awake: number }> {
  return shelterFeedSchema.parse(await authenticatedJson('/api/v1/shelter/feed'));
}

export async function getContentCards(): Promise<ContentCard[]> {
  let response: Response;
  try { response = await fetch(platform.apiUrl('/api/v1/content/cards')); } catch { throw new ApiFailure('OFFLINE', '인터넷 연결을 확인해 주세요.', 0); }
  if (!response.ok) throw await responseFailure(response);
  return contentFeedSchema.parse(await response.json()).cards;
}

export async function reactToPost(postId: string, kind: 'warm' | 'cheer' | 'hug'): Promise<void> {
  await authenticatedJson(`/api/v1/shelter/posts/${encodeURIComponent(postId)}/reaction`, { method: 'PUT', body: JSON.stringify({ kind }) });
}

export async function removeReaction(postId: string): Promise<void> {
  await authenticatedJson(`/api/v1/shelter/posts/${encodeURIComponent(postId)}/reaction`, { method: 'DELETE' });
}

export async function reportPost(postId: string, reason: 'abuse' | 'spam' | 'privacy' | 'self_harm' | 'etc'): Promise<void> {
  await authenticatedJson(`/api/v1/shelter/posts/${encodeURIComponent(postId)}/report`, { method: 'POST', body: JSON.stringify({ reason }) });
}

export async function deletePost(postId: string): Promise<void> {
  await authenticatedJson(`/api/v1/shelter/posts/${encodeURIComponent(postId)}`, { method: 'DELETE' });
}

export async function blockPostAuthor(postId: string): Promise<void> {
  await authenticatedJson('/api/v1/shelter/blocks', { method: 'POST', body: JSON.stringify({ postId }) });
}

export async function getBlockedAuthors(): Promise<ShelterBlock[]> {
  return shelterBlockListSchema.parse(await authenticatedJson('/api/v1/shelter/blocks')).items;
}

export async function unblockAuthor(blockId: string): Promise<void> {
  await authenticatedJson(`/api/v1/shelter/blocks/${encodeURIComponent(blockId)}`, { method: 'DELETE' });
}

export async function recognizeSchedule(image: File, yearMonth: string, hints: Array<{ id: string; short: string }>): Promise<OcrResponse> {
  const form = new FormData(); form.set('image', image); form.set('yearMonth', yearMonth); form.set('shiftTypeHints', JSON.stringify(hints));
  return ocrResponseSchema.parse(await authenticatedJson('/api/v1/ocr', { method: 'POST', body: form }));
}

export async function subscribeWebPush(subscription: PushSubscriptionJSON, preference: ReminderPreference): Promise<void> {
  await authenticatedJson('/api/v1/push/subscribe', { method: 'POST', body: JSON.stringify({ platform: 'web', subscription, ...preference }) });
}

export async function unsubscribeWebPush(): Promise<void> {
  await authenticatedJson('/api/v1/push/subscribe', { method: 'DELETE' });
}

export async function sendAnalyticsEvents(events: AnalyticsEvent[]): Promise<void> {
  await authenticatedJson('/api/v1/events', { method: 'POST', body: JSON.stringify({ events: events.map((event) => analyticsEventSchema.parse(event)) }) });
}

async function authenticatedJson(path: string, init: RequestInit = {}, retry = true): Promise<unknown> {
  const session = await ensureInstallSession();
  let response: Response;
  try {
    const headers = new Headers(init.headers);
    if (!(init.body instanceof FormData)) headers.set('Content-Type', 'application/json');
    headers.set('X-Install-Id', session.installId); headers.set('Authorization', `Install ${session.installToken}`);
    response = await fetch(platform.apiUrl(path), {
      ...init,
      headers,
    });
  } catch { throw new ApiFailure('OFFLINE', '인터넷 연결을 확인해 주세요.', 0); }
  if (response.status === 401 && retry) {
    await db.settings.delete('installSession');
    return authenticatedJson(path, init, false);
  }
  if (!response.ok) throw await responseFailure(response);
  return response.json();
}

async function ensureInstallSession(): Promise<StoredInstallSession> {
  const stored = await db.settings.get('installSession');
  if (isStoredSession(stored?.value) && stored.value.expiresAt > Date.now() + 60_000) return stored.value;
  let response: Response;
  try { response = await fetch(platform.apiUrl('/api/v1/install-sessions'), { method: 'POST' }); }
  catch { throw new ApiFailure('OFFLINE', '인터넷 연결을 확인해 주세요.', 0); }
  if (!response.ok) throw await responseFailure(response);
  const session = sessionResponseSchema.parse(await response.json());
  await db.settings.put({ key: 'installSession', value: session });
  return session;
}

async function responseFailure(response: Response): Promise<ApiFailure> {
  try {
    const value = await response.json() as { error?: { code?: string; message?: string } };
    return new ApiFailure(value.error?.code ?? 'UPSTREAM_ERROR', value.error?.message ?? '요청을 처리하지 못했어요.', response.status);
  } catch { return new ApiFailure('UPSTREAM_ERROR', '요청을 처리하지 못했어요.', response.status); }
}

function isStoredSession(value: unknown): value is StoredInstallSession {
  return sessionResponseSchema.safeParse(value).success;
}
